import { TrashCan16 } from "@carbon/icons-react";
import { Popconfirm, Switch } from "antd";
import { ColumnProps } from "antd/lib/table";
import { API_EMAIL_REMINDER_CONFIG_PREFIX } from "config/api-consts";
import { EMAIL_REMINDER_CONFIG_ROUTE } from "config/route-consts";
import { renderMasterIndex } from "helpers/table";
import { EmailRemindConfig } from "models/EmailRemindConfig";
import { EmailRemindConfigCalendar } from "models/EmailRemindConfig/EmailRemindConfigCalendar";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  FormItem,
  InputNumber,
  InputText,
  LayoutCell,
  LayoutHeader,
} from "react3l-ui-library";
import { emailRemindConfigRepository } from "repositories/email-remind-config-repository";
import { authorizationService } from "services/common-services/authorization-service";
import { utilService } from "services/common-services/util-service";
import {
  detailService,
  ModelActionEnum,
} from "services/page-services/detail-service";
import { fieldService } from "services/page-services/field-service";
import { filterService } from "services/page-services/filter-service";
import { listService } from "services/page-services/list-service";
import { masterService } from "services/page-services/master-service";
import { tableService } from "services/page-services/table-service";
import nameof from "ts-nameof.macro";

function useEmailRemindConfigDetailHook() {
  const [translate] = useTranslation();

  const { model, dispatch } =
    detailService.useModel<EmailRemindConfig>(EmailRemindConfig);

  const { isDetail } = detailService.useGetIsDetail<EmailRemindConfig>(
    emailRemindConfigRepository.get,
    dispatch
  );

  const { validAction } = authorizationService.useAuthorizedAction(
    EMAIL_REMINDER_CONFIG_ROUTE.split("/")[
      EMAIL_REMINDER_CONFIG_ROUTE.split("/").length - 1
    ],
    API_EMAIL_REMINDER_CONFIG_PREFIX
  );
  const { handleGoHistory } = masterService.useMasterAction(
    EMAIL_REMINDER_CONFIG_ROUTE
  );

  const {
    handleChangeAllField,
    handleChangeSingleField,
    handleChangeSelectField,
    handleChangeMappingField,
    handleChangeListField,
    handleChangeBoolField,
  } = fieldService.useField(model, dispatch);

  const handleChangeTemplateCode = useCallback(
    (value: string) => {
      dispatch({
        type: ModelActionEnum.UPDATE,
        payload: {
          applyFunction: {
            ...(model?.applyFunction || {}),
            templateCode: value,
          },
        } as any,
      });
    },
    [dispatch, model?.applyFunction]
  );

  const saveWithTemplateCode = useCallback((m: EmailRemindConfig) => {
    const cleaned: any = { ...m };
    if (cleaned.applyFunction) {
      const tc = cleaned.applyFunction.templateCode;
      if (!tc) {
        const { templateCode: _omit, ...rest } = cleaned.applyFunction;
        cleaned.applyFunction = rest;
      }
    }
    return emailRemindConfigRepository.save(cleaned);
  }, []);

  const { handleSaveModel, handleGoMaster, loading } =
    detailService.useActionsDetail<EmailRemindConfig>(
      model,
      saveWithTemplateCode,
      handleChangeAllField,
      EMAIL_REMINDER_CONFIG_ROUTE
    );

  const loadingDetail = loading || (isDetail && !model?.id);

  const {
    modelFilter: emailReminderConfigFilter,
    dispatchFilter: dispatchEmailRemindConfigFilter,
  } = filterService.useModelFilter(EmailRemindConfigCalendar);

  const { list: listEmailReminderSchedules } = listService.useLocalList(
    model?.emailReminderSchedules,
    emailReminderConfigFilter
  );

  const { handleChangeCell, handleDeleteRow } = tableService.useContentTable<
    EmailRemindConfigCalendar,
    any
  >(
    EmailRemindConfigCalendar,
    EmailRemindConfigCalendar,
    listEmailReminderSchedules,
    handleChangeListField({ fieldName: "emailReminderSchedules" }),
    dispatchEmailRemindConfigFilter
  );

  // const handleChangeTimeUnit = useCallback(
  //   (value: EmailRemindConfigCalendar) => (objectId: number, object: any) => {
  //     handleChangeRow(value.key, {
  //       ...value,
  //       timeUnitObj: object,
  //       timeUnitObjId: objectId,
  //     });
  //   },
  //   [handleChangeRow]
  // );

  const handleAddRow = useCallback(() => {
    listEmailReminderSchedules.unshift({
      ...new EmailRemindConfigCalendar(),
      timeUnit: "Day",
    });
    handleChangeListField({ fieldName: "emailReminderSchedules" })(
      listEmailReminderSchedules
    );
  }, [listEmailReminderSchedules, handleChangeListField]);

  const emailRemindConfigMappingColumns: ColumnProps<EmailRemindConfig>[] =
    useMemo(
      () => [
        {
          title: (
            <div className="text-center gradient-text">
              {translate("general.columns.orderNumber")}
            </div>
          ),
          key: "index",
          width: 80,
          align: "center",
          fixed: "left",
          render: renderMasterIndex<EmailRemindConfig>(),
        },
        {
          title: (
            <LayoutHeader
              orderType="left"
              title={translate("emailRemindConfig.reminderTable.timeAgo")}
            />
          ),
          key: "beforeValue",
          dataIndex: "beforeValue",
          render(...params: [number, EmailRemindConfigCalendar, number]) {
            return (
              <LayoutCell orderType="left" tableSize="lg">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    params[1],
                    "beforeValue"
                  )}
                  message={utilService.getValidateMessage(
                    params[1],
                    "beforeValue"
                  )}
                >
                  {" "}
                  <InputNumber
                    type={0}
                    value={params[0]}
                    min={0}
                    placeHolder={translate(
                      "emailRemindConfig.reminderTable.placeholder.timeAgo"
                    )}
                    onChange={(value: number) => {
                      handleChangeCell(params[1].key, "beforeValue", value);
                    }}
                  />
                </FormItem>
              </LayoutCell>
            );
          },
        },
        {
          title: (
            <LayoutHeader
              orderType="left"
              title={translate("emailRemindConfig.reminderTable.timeUnit")}
            />
          ),
          key: "timeUnit",
          dataIndex: "timeUnit",
          render(...params: [string, EmailRemindConfigCalendar, number]) {
            return (
              <LayoutCell orderType="left" tableSize="lg">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    params[1],
                    "timeUnit"
                  )}
                  message={utilService.getValidateMessage(
                    params[1],
                    "timeUnit"
                  )}
                >
                  <InputText
                    type={0}
                    value={params[0]}
                    placeHolder={translate(
                      "emailRemindConfig.reminderTable.placeholder.timeUnit"
                    )}
                    disabled
                  />
                  {/* <Select
                    isRequired
                    label={translate("emailRemindConfig.reminderTable.timeUnit")}
                    type={0}
                    classFilter={EmailRemindConfigFilter}
                    searchProperty={"name"}
                    placeHolder={translate(
                      "emailRemindConfig.reminderTable.placeholder.timeUnit"
                    )}
                    // getList={emailRemindConfigRepository.listTimeUnit}
                    onChange={handleChangeTimeUnit(params[1])}
                    value={model.timeUnit}
                  />{" "} */}
                </FormItem>
              </LayoutCell>
            );
          },
        },
        {
          title: (
            <LayoutHeader
              orderType="left"
              title={translate("emailRemindConfig.description")}
            />
          ),
          key: "description",
          dataIndex: "description",
          render(...params: [string, EmailRemindConfigCalendar, number]) {
            return (
              <LayoutCell orderType="left" tableSize="lg">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    params[1],
                    "description"
                  )}
                  message={utilService.getValidateMessage(
                    params[1],
                    "description"
                  )}
                >
                  {" "}
                  <InputText
                    type={0}
                    value={params[0]}
                    placeHolder={translate(
                      "emailRemindConfig.placeholder.description"
                    )}
                    onChange={(value: string) => {
                      handleChangeCell(params[1].key, "description", value);
                    }}
                  />
                </FormItem>
              </LayoutCell>
            );
          },
        },
        {
          title: (
            <LayoutHeader
              orderType="left"
              title={translate("emailRemindConfig.status")}
            />
          ),
          width: 120,
          key: nameof("order"),
          dataIndex: nameof("order"),
          align: "center",
          render(...params: [number, EmailRemindConfigCalendar, number]) {
            return (
              <LayoutCell orderType="center" tableSize="lg">
                <Switch
                  checked={params[1].statusId === 1 ? true : false}
                  onChange={(value: boolean) => {
                    handleChangeCell(params[1].key, "statusId", value ? 1 : 0);
                  }}
                />
              </LayoutCell>
            );
          },
        },
        {
          title: () => {
            return (
              <LayoutHeader
                orderType="left"
                title={translate("general.actions.label")}
              />
            );
          },
          key: nameof("action"),
          dataIndex: nameof("action"),
          fixed: "right",
          width: 100,
          align: "center",
          render(...params: [string, EmailRemindConfigCalendar, number]) {
            return (
              <div className="d-flex button-action-table missions-dxg">
                <div className="enable-dxg ml-1">
                  <Popconfirm
                    title={translate("general.warnings.delete")}
                    onConfirm={() => {
                      handleDeleteRow(params[1]?.key);
                    }}
                    okText={translate("general.actions.accept")}
                    cancelText={translate("general.actions.deny")}
                  >
                    <TrashCan16 className="btn-missions-red" />
                  </Popconfirm>
                </div>
              </div>
            );
          },
        },
      ],
      [handleChangeCell, handleDeleteRow, translate]
    );

  return {
    translate,
    validAction,
    handleGoHistory,
    handleGoMaster,
    model,
    isDetail,
    handleChangeAllField,
    handleChangeSingleField,
    handleChangeSelectField,
    handleChangeMappingField,
    handleChangeTemplateCode,
    emailRemindConfigMappingColumns,
    handleAddRow,
    listEmailReminderSchedules,
    handleSaveModel,
    loadingDetail,
    handleChangeStatus: handleChangeBoolField,
  };
}

export default useEmailRemindConfigDetailHook;
